/**
 * Reporting why the camera wouldn't start.
 *
 * Browsers raise `NotAllowedError` for a real refusal and also for "not now"
 * (page in the background, a prompt dismissed, the camera held elsewhere), and
 * the camera window shows the same message for all of them. These events say
 * which it was, from the devices where it happens.
 */
import posthog from "posthog-js";

import { isNativeHost } from "@/lib/native-bridge";

type CameraSource = "camera_window" | "barcode_scanner";

function isInstalledApp(): boolean {
  return (
    isNativeHost() ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true ||
    window.matchMedia?.("(display-mode: standalone)").matches === true
  );
}

function context() {
  return {
    installed_app: isInstalledApp(),
    visibility_state: document.visibilityState,
    has_focus: document.hasFocus(),
    // How long the page has been open: a failure hours in points at something
    // that built up over the day, one at start-up at the device's settings.
    minutes_since_page_load: Math.round(performance.now() / 60_000),
  };
}

export async function reportCameraStartFailed(
  source: CameraSource,
  err: unknown,
  extra: Record<string, unknown> = {},
): Promise<void> {
  const name = err instanceof Error ? err.name : "";
  const message = err instanceof Error ? err.message : String(err);
  let cameraPermission: string | null = null;
  try {
    const status = await navigator.permissions?.query({
      name: "camera" as PermissionName,
    });
    cameraPermission = status?.state ?? null;
  } catch {
    // Not every browser can be asked about the camera.
  }
  posthog.capture("camera_start_failed", {
    source,
    error_name: name,
    error_message: message,
    camera_permission: cameraPermission,
    ...context(),
    ...extra,
  });
}

export function reportCameraInterrupted(source: CameraSource): void {
  posthog.capture("camera_interrupted", { source, ...context() });
}
